import { useState } from 'react';
import { formatDate, formatTime } from '../../../lib/format';
import type { PartnerAvailability } from '@dru/types';
import { Alert, Button, ConfirmDialog, Dialog, SelectField, TextField, useIdempotencyKey } from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { firstFreeSlot, localDay, timesOn } from './ArrangeDialog';

/**
 * Moving a slot, and rebooking after a no-show.
 *
 * One dialog for both because the API treats them as one act: a reschedule
 * creates a new appointment at the same venue, marks the old one, and — when
 * the order was NO_SHOW_OPEN — returns it to SCHEDULED. Before this existed
 * the only way to move a booking was cancel-with-reason and then find the
 * worker again on the examinations list and book from scratch; and a no-show
 * could not be rebooked at all, because raising a fresh slot is refused for
 * that state.
 */
export function RescheduleDialog({ appointmentId, partnerId, partnerName, who, scheduledFor, mode, onClose, onDone }: {
  appointmentId: string;
  partnerId: string;
  partnerName?: string | null;
  who?: string | null;
  scheduledFor: string;
  mode: 'move' | 'rebook';
  onClose: () => void;
  onDone: () => void;
}) {
  // The next slot still ahead, not 09:30 today whatever the time: a time
  // already gone is refused by the server after the dialog said nothing.
  const [first] = useState(firstFreeSlot);
  const [date, setDate] = useState(first.date);
  const [time, setTime] = useState(first.time);
  // A day already gone has no times at all.
  const times = date && date >= localDay() ? timesOn(date) : [];
  const chosenTime = times.includes(time) ? time : times[0] ?? '';
  const [reason, setReason] = useState(mode === 'rebook' ? 'Rebooked after a missed appointment' : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idem = useIdempotencyKey();
  // The venue's capacity is read from the network, which a plant login may
  // not browse. Without it the booking still goes through — the API refuses
  // a full day with a sentence — it just is not forecast here.
  const { permissions } = useAuth();
  const readsNetwork = permissions.has('partner.read');

  const avail = useQuery<PartnerAvailability | null>(
    () => (readsNetwork ? api.get(`/partners/${partnerId}/availability?date=${date}`) : Promise.resolve(null)),
    [partnerId, date, readsNetwork],
  );

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/appointments/${appointmentId}/reschedule`, {
        scheduledFor: new Date(`${date}T${chosenTime}:00`).toISOString(),
        reason: reason.trim(),
      }, idem.key());
      idem.rotate();
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  const old = new Date(scheduledFor);
  const fresh = date && chosenTime ? new Date(`${date}T${chosenTime}:00`) : null;
  const unchanged = fresh ? fresh.getTime() === old.getTime() : false;

  return (
    <Dialog title={mode === 'rebook' ? 'Rebook after a no-show' : 'Reschedule this appointment'} onClose={onClose}>
      <p className="mt-1 text-small text-neutral-600">
        {who ?? 'The employee'} {mode === 'rebook' ? 'missed' : 'is booked for'}{' '}
        {formatDate(old)} at {formatTime(old)}
        {partnerName ? ` at ${partnerName}` : ''}. The new slot is at the same venue; the old one is
        kept on the record as {mode === 'rebook' ? 'missed' : 'moved'}.
      </p>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <TextField label="New date" type="date" value={date} onChange={setDate} required />
        <SelectField label="Time" value={chosenTime} onChange={setTime} required disabled={!times.length}>
          {times.length
            ? times.map((t) => <option key={t} value={t}>{t}</option>)
            : <option value="">No times left on this day</option>}
        </SelectField>
      </div>
      {!times.length && date && <p className="mt-2 text-xs text-overdue-600">No times are left on this day. Choose another day.</p>}

      {avail.data && (
        <p className={`mt-2 text-xs ${avail.data.full ? 'text-overdue-600' : 'text-neutral-600'}`}>
          {avail.data.full
            ? `${avail.data.partnerName} is full on this date (${avail.data.booked} of ${avail.data.dailyCapacity}). Choose another day.`
            : `${avail.data.remaining} of ${avail.data.dailyCapacity} slots left on this date.`}
        </p>
      )}

      <TextField label="Reason" value={reason} onChange={setReason} required className="mt-3"
        hint="At least five characters."
        info="Recorded on both appointments and in the audit trail." />

      {fresh && (
        <dl className="mt-3 grid grid-cols-[4rem_1fr] gap-x-3 gap-y-1 rounded-card border border-neutral-200 bg-neutral-50 px-3 py-2 text-small">
          <dt className="text-neutral-500">From</dt>
          <dd className="text-neutral-600 line-through decoration-neutral-400">{formatDate(old)}, {formatTime(old)}</dd>
          <dt className="text-neutral-500">To</dt>
          <dd className="font-medium text-neutral-900">{formatDate(fresh)}, {formatTime(fresh)}{partnerName ? ` · ${partnerName}` : ''}</dd>
        </dl>
      )}

      {error && <Alert className="mt-3">{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}
          disabled={reason.trim().length < 5 || avail.data?.full || !chosenTime || unchanged}>
          {mode === 'rebook' ? 'Confirm rebooking' : 'Confirm new time'}
        </Button>
      </div>
    </Dialog>
  );
}

/** "Did not attend" — HR's copy of the desk's act, for a slot that has passed. */
export async function markNoShow(appointmentId: string) {
  await api.post(`/appointments/${appointmentId}/no-show`, {});
}

/**
 * Confirming a no-show before it is recorded.
 *
 * It was one click on a list row, beside "Move" and "Cancel", and it moves the
 * case into "missed" and the worker onto the rebooking list. Said first,
 * then done.
 */
export function NoShowDialog({ appointmentId, who, when, onClose, onDone }: {
  appointmentId: string; who?: string | null; when: string; onClose: () => void; onDone: () => void;
}) {
  const at = new Date(when);
  return (
    <ConfirmDialog
      title="Record that the employee did not attend"
      what={<>
        {who ?? 'The employee'} is recorded as not attending the slot on {formatDate(at)} at{' '}
        {formatTime(at)}.
      </>}
      affects="The medical case moves to “Missed — to rebook” and stays open. The examination is still owed."
      irreversible="Only do this once the slot has passed and the venue confirms nobody came."
      confirmLabel="Record did not attend"
      tone="danger"
      onConfirm={async () => {
        try { await markNoShow(appointmentId); } catch (e) { throw new Error(errorMessage(e)); }
        onDone();
      }}
      onCancel={onClose}
    />
  );
}

/**
 * Cancelling an examination raised in error. Distinct from cancelling a
 * slot, which leaves the examination due; this closes the order itself, and
 * the worker drops out of the pipeline until a new one is raised.
 */
export function CancelExaminationDialog({ orderId, who, onClose, onDone }: {
  orderId: string; who?: string | null; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per attempt, kept across a retry, so a double submit is one cancel.
  const idem = useIdempotencyKey();

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/examinations/${orderId}/cancel`, { reason: reason.trim() }, idem.key());
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog title="Cancel this medical case" onClose={onClose}>
      <p className="mt-1 text-small text-neutral-600">
        Closes the medical case for {who ?? 'this employee'}. Any booked slot is released. The employee stays in
        the compliance figures as owed an examination — cancelling does not make them covered.
      </p>
      <Alert tone="warning" className="mt-3">
        This cannot be undone. To examine them later, arrange a new medical case.
      </Alert>
      <TextField label="Reason" value={reason} onChange={setReason} required autoFocus className="mt-3"
        placeholder="Raised in error, duplicate, employee exited…"
        hint="At least five characters."
        info="Recorded on the order and in the audit trail." />
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Keep it</Button>
        <Button variant="danger" onClick={submit} loading={busy} disabled={reason.trim().length < 5}>
          Cancel medical case
        </Button>
      </div>
    </Dialog>
  );
}
